<template>
  <div
    class="profile-history-chart"
    :class="{
      'profile-history-chart--compact': compact,
      'profile-history-chart--legacy': legacyLayout,
    }"
  >
    <div
      ref="chartContainer"
      :id="chartContainerId"
      class="profile-history-chart-container"
    >
      <canvas :id="chartCanvasId"></canvas>
    </div>
  </div>
</template>

<script>
import { Chart } from 'chart.js'
import moment from 'moment'
import { getStandardizedLevelAxisRange } from '../utils/standardizedLevelAxisRange'

export default {
  name: "SiloProfileHistoryChart",
  props: {
    silo_profiles: {
      type: Array,
      default: () => []
    },
    compact: {
      type: Boolean,
      default: false
    },
    legacyLayout: {
      type: Boolean,
      default: false
    },
    hideTitle: {
      type: Boolean,
      default: false
    },
    startDate: {
      type: [String, Date, Number],
      default: null,
    },
    endDate: {
      type: [String, Date, Number],
      default: null,
    },
  },
  data() {
    return {
      chartContainerId: '',
      chartCanvasId: '',
      chartInstance: null,
      containerWidth: 0,
      resizeObserver: null,
      resizeHandler: null,
    }
  },
  computed: {
    isNarrowLayout() {
      return this.legacyLayout && this.containerWidth > 0 && this.containerWidth < 900;
    },
    maxProfileLevel() {
      if (!this.silo_profiles?.length) return 0;
      return Math.max(
        ...this.silo_profiles.map((profile) => Number(profile.y) || 0)
      );
    },
    yAxisRange() {
      return getStandardizedLevelAxisRange(this.maxProfileLevel);
    },
    xAxisBounds() {
      const start = this.parseChartDate(this.startDate);
      const end = this.parseChartDate(this.endDate);

      if (start && end && start.getTime() < end.getTime()) {
        return { min: start, max: end };
      }

      if (!this.legacyLayout || !this.silo_profiles?.length) return null;

      const timestamps = this.silo_profiles
        .map(profile => this.parseChartDate(profile.x)?.getTime())
        .filter(Number.isFinite);

      if (!timestamps.length) return null;

      return {
        min: new Date(Math.min(...timestamps)),
        max: new Date(Math.max(...timestamps)),
      };
    },
    xAxisTimeFormat() {
      return this.isNarrowLayout ? 'DD/MM HH:mm' : 'DD/MM/YY - H:mm';
    },
    xAxisTimeUnit() {
      const bounds = this.xAxisBounds;
      if (!bounds) return 'day';

      const rangeMs = bounds.max.getTime() - bounds.min.getTime();
      const rangeDays = rangeMs / (1000 * 60 * 60 * 24);

      if (rangeDays <= 2) return 'hour';
      if (rangeDays <= 21) return 'day';
      return 'week';
    },
  },
  methods: {
    parseChartDate(value) {
      if (value == null || value === '') return null;
      if (value instanceof Date) {
        return Number.isFinite(value.getTime()) ? value : null;
      }

      const parsed = moment(
        value,
        [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss'],
        true
      );

      if (parsed.isValid()) {
        return parsed.toDate();
      }

      const fallback = new Date(value);
      return Number.isFinite(fallback.getTime()) ? fallback : null;
    },
    formatTooltipDate(value) {
      const parsed = this.parseChartDate(value);
      return parsed ? moment(parsed).format('DD/MM/YY - H:mm:ss') : '';
    },
    buildXAxisConfig() {
      if (this.legacyLayout) {
        return [{
          type: 'time',
          offset: false,
          time: {
            unit: this.xAxisTimeUnit,
            tooltipFormat: 'DD/MM/YY - H:mm:ss',
            displayFormats: {
              millisecond: this.xAxisTimeFormat,
              second: this.xAxisTimeFormat,
              minute: this.xAxisTimeFormat,
              hour: this.xAxisTimeFormat,
              day: this.xAxisTimeFormat,
              week: this.xAxisTimeFormat,
              month: this.xAxisTimeFormat,
            },
          },
          gridLines: {
            drawBorder: false,
            offsetGridLines: false,
          },
          ticks: {
            min: this.xAxisBounds?.min ? moment(this.xAxisBounds.min) : undefined,
            max: this.xAxisBounds?.max ? moment(this.xAxisBounds.max) : undefined,
            padding: this.isNarrowLayout ? 6 : 6,
            maxRotation: this.isNarrowLayout ? 0 : 40,
            minRotation: this.isNarrowLayout ? 0 : 40,
            autoSkip: true,
            fontSize: this.isNarrowLayout ? 9 : 9,
            maxTicksLimit: this.isNarrowLayout ? 3 : 6,
            major: {
              enabled: true,
            },
          },
        }];
      }

      return [{
        offset: false,
        gridLines: {
          drawBorder: false,
          offsetGridLines: false,
        },
        ticks: {
          min: this.xAxisBounds ? this.xAxisBounds.min.getTime() : undefined,
          max: this.xAxisBounds ? this.xAxisBounds.max.getTime() : undefined,
          padding: 2,
          maxRotation: 40,
          minRotation: 40,
          fontSize: 10,
          userCallback(label) {
            return moment(label).format('DD/MM/YY - H:mm');
          },
        },
      }];
    },
    buildYAxisConfig() {
      if (this.legacyLayout) {
        return [{
          gridLines: {
            drawBorder: false,
          },
          ticks: {
            min: this.yAxisRange.min,
            max: this.yAxisRange.max,
            stepSize: this.yAxisRange.stepSize,
            autoSkip: false,
            padding: this.isNarrowLayout ? 4 : 0,
            fontSize: 10,
            maxTicksLimit: 7,
          },
          afterFit: (scale) => {
            scale.width = this.isNarrowLayout ? 34 : 26;
          },
        }];
      }

      return [{
        gridLines: {
          drawBorder: false,
        },
        ticks: {
          min: this.yAxisRange.min,
          max: this.yAxisRange.max,
          stepSize: this.yAxisRange.stepSize,
          autoSkip: false,
          padding: 0,
          fontSize: 10,
          maxTicksLimit: 7,
        },
        afterFit(scale) {
          scale.width = 26;
        },
      }];
    },
    buildLayoutPadding() {
      if (this.compact) {
        return { left: 0, right: 6, top: 4, bottom: 0 };
      }

      if (this.legacyLayout) {
        return this.isNarrowLayout
          ? { left: 0, right: 8, top: 8, bottom: 28 }
          : { left: 4, right: 8, top: 4, bottom: 42 };
      }

      return { left: 0, right: 8, top: 4, bottom: 0 };
    },
    measureContainer() {
      const container = this.$refs.chartContainer;
      if (!container) return false;

      const rect = container.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);

      if (width <= 0 || height <= 0) return false;

      this.containerWidth = width;
      return { width, height };
    },
    setupResizeObserver() {
      const container = this.$refs.chartContainer;
      if (!container) return;

      const updateSize = () => {
        const dimensions = this.measureContainer();
        if (!dimensions) return;

        if (this.chartInstance) {
          this.chartInstance.destroy();
          this.chartInstance = null;
        }
        this.renderChart(dimensions);
      };

      updateSize();

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(updateSize);
        this.resizeObserver.observe(container);
      } else {
        this.resizeHandler = updateSize;
        window.addEventListener('resize', this.resizeHandler);
      }
    },
    teardownResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
        this.resizeHandler = null;
      }
    },
    renderChart(dimensionsOverride = null) {
      const canvasElement = document.getElementById(this.chartCanvasId);
      const canvasContainer = this.$refs.chartContainer;

      if (!canvasElement || !canvasContainer) {
        return;
      }

      const dimensions = dimensionsOverride || this.measureContainer();
      if (!dimensions) return;

      var canvasContext = canvasElement.getContext('2d');
      canvasContext.canvas.width = dimensions.width;
      canvasContext.canvas.height = dimensions.height;

      var ctx = document.getElementById(this.chartCanvasId);
      this.chartInstance = new Chart(ctx, {
        type: 'scatter',
        data: {
            datasets: [{
                label: 'Nivel do silo',
                data: this.silo_profiles,
                showLine: true,
                tension: 0,
                fill: this.legacyLayout ? 'origin' : false,
                backgroundColor: this.legacyLayout ? 'rgba(137, 194, 91, 0.18)' : undefined,
            }]
        },
        options: {
          animation: false,
          legend: {
            display: false
          },
          hover: {
            mode: 'index',
            intersect: false
          },
          scales: {
            xAxes: this.buildXAxisConfig(),
            yAxes: this.buildYAxisConfig(),
          },
          layout: {
            padding: this.buildLayoutPadding(),
          },
          elements: {
            point: {
              radius: 2,
              backgroundColor: '#89c25b',
              borderColor: '#89c25b',
              borderWidth: 1,
            },
            line: {
              tension: 0.1,
              borderColor: '#89c25b',
              borderWidth: 2
            }
          },
          tooltips: {
            intersect: false,
            mode: 'index',
            callbacks: {
              label: (tooltipItem) => {
                const dataIndex = tooltipItem.index;
                const profile = this.silo_profiles[dataIndex];
                const hasPreCharge = profile?.preCharge;

                if (hasPreCharge) {
                  return 'Perfil pre-abastecimento';
                }

                const level = Number(profile?.y ?? tooltipItem.yLabel);
                const dateValue = profile?.x ?? tooltipItem.xValue ?? tooltipItem.xLabel;

                return `Nivel: ${level.toFixed(2)} | Data-hora: ${this.formatTooltipDate(dateValue)}`;
              }
            },
            backgroundColor: '#fff',
            titleFontSize: 16,
            titleFontColor: '#89c25b',
            bodyFontColor: '#000',
            bodyFontSize: 14,
            displayColors: false,
            borderColor: '#89c25b',
            borderWidth: 2,
          },
          title: {
            display: !this.compact && !(this.legacyLayout && this.hideTitle) && !this.isNarrowLayout,
            text: 'Historico de nivel',
            fontSize: this.compact ? 14 : 16,
            padding: 4,
          },
          responsive: true,
          maintainAspectRatio: false,
          stroke: {
            curve: 'straight'
          },
          onHover: (event, activeElements) => {
            if (activeElements && activeElements.length > 0) {
              const dataIndex = activeElements[0]._index;
              const profile = this.silo_profiles[dataIndex];
              if (profile) {
                this.$emit('profileHover', profile);
              }
            }
          }
        },
      });
    },
    updateChartData() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      const dimensions = this.measureContainer();
      if (dimensions) {
        this.renderChart(dimensions);
      }
    }
  },
  watch: {
    silo_profiles: {
      handler() {
        if (!this.silo_profiles || this.silo_profiles.length === 0) {
          return;
        }

        const canvasElement = document.getElementById(this.chartCanvasId);
        if (!canvasElement || !this.$refs.chartContainer) {
          return;
        }

        if (this.chartInstance) {
          this.updateChartData();
        } else {
          const dimensions = this.measureContainer();
          if (dimensions) {
            this.renderChart(dimensions);
          }
        }
      },
      deep: false,
    },
    startDate() {
      if (!this.legacyLayout) return;
      this.updateChartData();
    },
    endDate() {
      if (!this.legacyLayout) return;
      this.updateChartData();
    },
  },
  beforeMount () {
    this.chartContainerId = `profile-history-canvas-container-${Math.random()}`
    this.chartCanvasId = `silo-profile-history-chart-${Math.random()}`
  },
  mounted () {
    this.setupResizeObserver();
  },
  beforeDestroy () {
    this.teardownResizeObserver();
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  },
}
</script>

<style scoped>
.profile-history-chart {
  width: 100%;
  height: 100%;
  min-height: 280px;
}

.profile-history-chart-container {
  width: calc(100% + 10px);
  height: 100%;
  margin-left: -10px;
}

.profile-history-chart--legacy .profile-history-chart-container {
  width: 100%;
  margin-left: 0;
}

.profile-history-chart--legacy {
  min-height: 0;
  height: 100%;
}

.profile-history-chart--compact {
  min-height: 200px;
  height: 100%;
}
</style>
